import React from 'react';
import { ImageBackground, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  Button,
  Container,
  getContentfulImageUrl,
  theme,
  TitleText,
  useBreakpointHelpers,
} from '@sg/garnish';

import type { SweetpassUpgradeScreenPalette } from './SweetgreenUpgradeScreenPalette';
import type {
  SweetpassUpgradeCtaHandler,
  SweetpassUpgradeHeader,
} from './SweetpassUpgradeScreen.loaded';

type SweetpassUpgradeHeaderContentProps = {
  readonly content: SweetpassUpgradeHeader;
  readonly palette: SweetpassUpgradeScreenPalette['header'];
  readonly ctaHandler: SweetpassUpgradeCtaHandler;
};
export const SweetpassUpgradeHeaderContent = ({
  content,
  palette,
  ctaHandler,
}: SweetpassUpgradeHeaderContentProps) => {
  const { isMobile, isTablet, matchByBreakpointIndex } = useBreakpointHelpers();

  const handlePressCta = React.useCallback(() => {
    ctaHandler(content.cta.action);
  }, [ctaHandler, content]);

  const heroImageUrl = content.heroImage.url;

  const headerStyle = useStyle(
    () => ({
      backgroundColor: palette.backgroundColor,
      color: palette.titleColor,
    }),
    [palette],
  );

  return (
    <View style={[styles.screenWideHeader, headerStyle]}>
      <Container testID="sweetpass-upgrade-header" customSpacing={0}>
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <ImageBackground
            style={[
              { marginBottom: theme.spacing.xlarge },
              isMobile
                ? styles.headerImageMobile
                : isTablet
                ? styles.headerImageTablet
                : styles.headerImageDesktop,
            ]}
            source={{
              uri: getContentfulImageUrl(heroImageUrl, {
                fm: 'webp',
                q: 90,
                w: 308,
                h: 245,
                fit: 'fill',
              }),
            }}
          />
          <TitleText style={[styles.headerText, headerStyle]} size={0}>
            {content.headingText.toUpperCase()}
          </TitleText>
          <BodyText
            style={[styles.captionText, headerStyle]}
            size={matchByBreakpointIndex([2, 1])}
          >
            {content.bodyText}
          </BodyText>
          {!isMobile ? (
            <Button
              style={styles.cta}
              testID="sweetpass.upgrade-header-cta"
              size="xlarge"
              palette={palette.ctaPalette}
              onPress={handlePressCta}
            >
              {content.cta.title}
            </Button>
          ) : null}
        </View>
      </Container>
    </View>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing.xxlarge,
  },
  headerMobile: {
    padding: theme.spacing.medium,
  },
  headerImageDesktop: {
    height: 245,
    width: 308,
  },
  headerImageTablet: {
    height: 183,
    width: 230,
  },
  headerImageMobile: {
    height: 147,
    width: 184,
  },
  headerText: {
    paddingBottom: theme.spacing.small,
  },
  captionText: {
    paddingBottom: theme.spacing.xlarge,
  },
  screenWideHeader: {
    width: '100%',
  },
  cta: {
    minWidth: 520,
  },
});
